import { useMemo, useState } from "react";
import { Share2 } from "lucide-react";
import { Slider } from "../ui/slider";
import { toast } from "../ui/sonner";

const TIERS = [0, 100, 500, 2000, 10000];
const POP_M = 69.08;
const DEFICIT = 152.5;
const BESOIN = 302.5;
const fmt = (n, d = 1) => n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: d });

const Result = ({ label, value, sub, accent, testid }) => (
  <div className={`border-t-4 pt-4 ${accent ? "border-mm-red" : "border-mm-paper"}`}>
    <p className="mm-lab text-mm-goldlight">{label}</p>
    <p className={`disp text-3xl xl:text-4xl mt-2 tab ${accent ? "text-mm-goldlight" : ""}`} data-testid={testid}>{value}</p>
    {sub && <p className="text-sm mt-1 text-mm-cream">{sub}</p>}
  </div>
);

const Gauge = ({ pct, label }) => (
  <div>
    <div className="flex justify-between text-sm"><span>{label}</span><span className="font-bold tab">{fmt(pct)} %</span></div>
    <div className="h-3 bg-mm-paper/15 mt-2"><div className="h-full bg-mm-gold transition-[width] duration-500" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
  </div>
);

const readParams = () => {
  const q = new URLSearchParams(window.location.search);
  const m = Number(q.get("m"));
  const p = Number(q.get("p"));
  return {
    m: Number.isFinite(m) && m >= 0 && q.has("m") ? Math.min(m, 1000000) : 500,
    p: Number.isFinite(p) && p >= 0.5 && p <= POP_M ? p : 10,
  };
};

const ShareSim = ({ amount, people, r }) => {
  const url = `${window.location.origin}/?m=${amount}&p=${people}#simulateur`;
  const text = `Si ${fmt(people)} million${people >= 2 ? "s" : ""} de personnes donnaient ${fmt(amount, 0)} € par an, la France rassemblerait ${fmt(r.annual)} Md€ par an — ${fmt(r.def)} % du déficit annuel. Et vous, quelle serait votre contribution ? #LaGrandeQuestion`;
  const links = [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}` },
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  ];
  const copy = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Simulateur France Avenir", text, url }); } catch { /* annulé */ }
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success("Votre simulation est copiée. Collez-la où vous voulez.");
  };
  return (
    <div className="mt-8 pt-6 border-t border-mm-paper/20" data-testid="sim-share">
      <p className="mm-lab text-mm-goldlight">Partager ma simulation</p>
      <p className="text-sm mt-2 text-mm-cream italic max-w-[70ch]" data-testid="sim-share-text">« {text} »</p>
      <div className="flex flex-wrap gap-3 mt-4">
        {links.map((l) => (
          <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`sim-share-${l.id}`}
            className="px-4 py-2 font-bold border-2 border-mm-paper hover:bg-mm-paper hover:text-mm-ink transition-colors">{l.label}</a>
        ))}
        <button type="button" onClick={copy} data-testid="sim-share-copy" className="inline-flex items-center gap-2 px-4 py-2 font-bold bg-mm-gold text-mm-ink hover:bg-mm-paper transition-colors">
          <Share2 className="w-4 h-4" /> Copier / partager
        </button>
      </div>
    </div>
  );
};

export const Simulateur = () => {
  const [init] = useState(readParams);
  const [amount, setAmount] = useState(init.m);
  const [people, setPeople] = useState(init.p);
  const r = useMemo(() => {
    const annual = (amount * people * 1e6) / 1e9;
    return { annual, ten: annual * 10, def: (annual / DEFICIT) * 100, bes: (annual / BESOIN) * 100 };
  }, [amount, people]);

  return (
    <div id="simulateur" className="bg-mm-ink text-mm-paper p-6 md:p-10 scroll-mt-24" data-testid="simulateur">
      <p className="mm-lab text-mm-goldlight">Simulateur France Avenir</p>
      <h3 className="disp text-3xl md:text-4xl mt-3">Et si chacun participait ?</h3>
      <p className="mt-3 text-mm-cream max-w-[62ch]">Choisissez une contribution et un nombre de participants. La règle de trois fait le reste.</p>
      <div className="grid lg:grid-cols-2 gap-10 mt-8">
        <div className="space-y-8">
          <div>
            <p className="mm-lab">Ma contribution par an</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {TIERS.map((t) => (
                <button key={t} type="button" onClick={() => setAmount(t)} data-testid={`sim-tier-${t}`}
                  className={`px-4 py-2 font-bold border-2 transition-colors ${amount === t ? "bg-mm-red border-mm-red text-white" : "border-mm-paper hover:bg-mm-paper hover:text-mm-ink"}`}>
                  {fmt(t, 0)} €
                </button>
              ))}
            </div>
            <label className="flex items-center gap-3 mt-4 text-sm">Autre montant
              <input type="number" min={0} max={1000000} value={amount} onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                data-testid="sim-amount-input" className="w-32 bg-transparent border-b-2 border-mm-paper py-1 outline-none focus:border-mm-gold tab" /> € / an
            </label>
          </div>
          <div>
            <div className="flex justify-between"><p className="mm-lab">Nombre de participants</p><p className="font-bold tab" data-testid="sim-people-value">{fmt(people)} million{people >= 2 ? "s" : ""}</p></div>
            <Slider value={[people]} min={0.5} max={POP_M} step={0.5} onValueChange={([v]) => setPeople(v)} data-testid="sim-people-slider"
              className="mt-4 [&_[role=slider]]:bg-mm-gold [&_[role=slider]]:border-mm-gold [&>span:first-child]:bg-mm-paper/20 [&>span:first-child>span]:bg-mm-gold" />
            <p className="text-xs mt-2 text-mm-cream">Sur ≈ 69,08 millions d'habitants (INSEE, 1er janvier 2026).</p>
          </div>
        </div>
        <div className="space-y-8">
          <div className="grid sm:grid-cols-2 gap-6">
            <Result label="La France rassemblerait" value={`${fmt(r.annual)} Md€ / an`} accent testid="sim-total-annual" />
            <Result label="Sur 10 ans" value={`${fmt(r.ten)} Md€`} sub={`Votre part : ${fmt(amount * 10, 0)} €`} testid="sim-total-ten" />
          </div>
          <Gauge pct={r.def} label="du déficit annuel (152,5 Md€)" />
          <Gauge pct={r.bes} label="du besoin total simulé (302,5 Md€ / an)" />
        </div>
      </div>
      <ShareSim amount={amount} people={people} r={r} />
      <p className="text-xs mt-8 text-mm-cream">Simulation pédagogique : aucun paiement ni engagement sur ce site.</p>
    </div>
  );
};
